import { useState, useEffect } from 'react';
import { Video } from '@fake-stack-overflow/shared';
import useUserContext from './useUserContext';

const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

const useWatchPartyQueue = (watchPartyId: string, host: string) => {
  const { user, socket } = useUserContext();
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [queue, setQueue] = useState<Video[]>([]);
  const [message, setMessage] = useState<string>('');

  // State for manual video input
  const [manualLink, setManualLink] = useState('');
  const [loadingTitle, setLoadingTitle] = useState(false);

  useEffect(() => {
    socket.emit('joinWatchParty', watchPartyId, user.username);

    socket.on('queueState', (currentQueue: Video[]) => {
      setCurrentVideo(currentQueue[0]);
      setQueue(currentQueue);
    });

    socket.on('newVideo', (data: { video: Video }) => {
      setQueue(prevQueue => [...prevQueue, data.video]);
    });

    socket.on('removeVideo', (data: { video: Video }) => {
      setQueue(prevQueue => prevQueue.filter(v => v.url !== data.video.url));
      if (currentVideo?.url === data.video.url) {
        setCurrentVideo(queue[0] || null);
      }
    });

    return () => {
      socket.off('queueState');
      socket.off('newVideo');
      socket.off('removeVideo');
    };
  }, [currentVideo, queue, socket, user.username, watchPartyId]);

  const isValidYouTubeUrl = (url: string) => {
    const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return pattern.test(url);
  };

  const extractVideoId = (url: string) => {
    const match = url.match(
      /(?:youtube\.com\/.*[?&]v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/watch\?v=)([^&?#]+)/,
    );
    return match ? match[1] : null;
  };

  const fetchVideoTitle = async (videoId: string) => {
    setLoadingTitle(true);
    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&part=snippet&key=${YOUTUBE_API_KEY}`,
      );
      const data = await response.json();
      if (data.items && data.items.length > 0) {
        return data.items[0].snippet.title;
      }
    } catch (error) {
      /* empty */
    } finally {
      setLoadingTitle(false);
    }
    return 'Unknown Title';
  };

  const addVideo = (video: Video) => {
    if (user.username !== host) {
      setMessage('Only the host can add videos.');
      return;
    }
    setQueue(prevQueue => [...prevQueue, video]);
    if (!currentVideo) {
      setCurrentVideo(video);
    }
    socket.emit('addVideoToQueue', { watchPartyId, data: video });
    setMessage('');
  };

  const removeVideo = (video: Video) => {
    if (user.username !== host) {
      setMessage('Only the host can remove videos.');
      return;
    }
    setQueue(prevQueue => prevQueue.filter(v => v.url !== video.url));
    if (currentVideo?.url === video.url) {
      setCurrentVideo(queue[0] || null);
    }
    socket.emit('removeVideoFromQueue', { watchPartyId, data: video });
    setMessage('');
  };

  const clearMessage = () => {
    setMessage('');
  };

  const nextVideo = () => {
    removeVideo(currentVideo as Video);
  };

  const handleAddManualVideo = async () => {
    if (!isValidYouTubeUrl(manualLink)) {
      // eslint-disable-next-line no-alert
      alert('Please provide a valid YouTube URL.');
      return;
    }

    const videoId = extractVideoId(manualLink);
    if (!videoId) {
      // eslint-disable-next-line no-alert
      alert('Invalid YouTube URL.');
      return;
    }

    const title = await fetchVideoTitle(videoId);
    const video = { title, url: manualLink };

    addVideo(video);
    setManualLink('');
  };

  return {
    queue,
    currentVideo,
    message,
    addVideo,
    removeVideo,
    clearMessage,
    nextVideo,
    handleAddManualVideo,
    manualLink,
    setManualLink,
    loadingTitle,
  };
};

export default useWatchPartyQueue;
